import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart3, 
  Mail, 
  TrendingUp, 
  Users,
  Radio,
  Search,
  Share2,
  UserPlus,
  Newspaper,
  Calendar,
  MoreHorizontal,
  Eye,
  CheckCircle,
  Globe,
  ShieldAlert,
  EyeOff
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = "";

// Source icons mapping - updated for specific radio stations
const sourceIcons = {
  kahl_radio: Radio,
  kpac_radio: Radio,
  google: Search,
  social: Share2,
  radio: Radio,  // Legacy
  friend: UserPlus,
  website: Globe,
  newsletter: Newspaper,
  event: Calendar,
  other: MoreHorizontal
};

// Source colors for the bar chart - radio stations get warm colors
const sourceColors = {
  kahl_radio: 'bg-amber-500',
  kpac_radio: 'bg-orange-500',
  google: 'bg-blue-500',
  social: 'bg-pink-500',
  radio: 'bg-amber-400',  // Legacy
  friend: 'bg-green-500',
  website: 'bg-indigo-500',
  newsletter: 'bg-purple-500',
  event: 'bg-cyan-500',
  other: 'bg-gray-500'
};

export default function AdminContactAnalytics() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [includeSpam, setIncludeSpam] = useState(false);
  const [spamTarget, setSpamTarget] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token, includeSpam]);

  const fetchData = async () => {
    if (!token) return;
    try {
      const subsUrl = `${API_URL}/api/contact/submissions?limit=20${includeSpam ? '&include_spam=true' : ''}`;
      const [analyticsRes, submissionsRes] = await Promise.all([
        fetch(`${API_URL}/api/contact/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(subsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok && submissionsRes.ok) {
        setAnalytics(await analyticsRes.json());
        setSubmissions(await submissionsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/api/contact/submissions/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        toast.success(`Marked as ${status}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const confirmMarkSpam = async () => {
    if (!spamTarget) return;
    await updateStatus(spamTarget.id, 'spam');
    setSpamTarget(null);
    if (selectedSubmission?.id === spamTarget.id) setSelectedSubmission(null);
  };

  const unmarkSpam = (id) => {
    updateStatus(id, 'read');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Contact Analytics" subtitle="Track how visitors find the academy">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
        </div>
      </AdminLayout>
    );
  }

  const maxCount = analytics?.by_source?.[0]?.count || 1;

  return (
    <AdminLayout title="Contact Analytics" subtitle="Track how visitors find the academy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="total-submissions-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cafa-red-100 rounded-full flex items-center justify-center">
                  <Mail className="text-cafa-red" size={24} />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Submissions</p>
                  <p className="text-3xl font-bold text-stone-900">{analytics?.total_submissions || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="top-source-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Top Source</p>
                  <p className="text-xl font-bold text-stone-900">
                    {analytics?.by_source?.[0]?.label || 'N/A'}
                  </p>
                  <p className="text-sm text-green-600">
                    {analytics?.by_source?.[0]?.percentage || 0}% of submissions
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="unique-sources-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Unique Sources</p>
                  <p className="text-3xl font-bold text-stone-900">
                    {analytics?.by_source?.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Radio Conversions Dashboard */}
        {analytics?.radio_stats && (
          <Card className="mb-8 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="radio-roi-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Radio size={20} />
                📻 Radio Conversions Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Main Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-white rounded-lg border-2 border-amber-300 shadow-sm">
                  <p className="text-3xl font-bold text-amber-700">
                    {analytics.radio_stats.total_radio_attributed}
                  </p>
                  <p className="text-xs text-stone-600 font-medium">Total Radio Leads</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-orange-300">
                  <p className="text-2xl font-bold text-orange-600">
                    {analytics.radio_stats.kpac_count + (analytics.radio_stats.welcome_page_conversions || 0)}
                  </p>
                  <p className="text-xs text-stone-500">KPAC Radio</p>
                  <p className="text-[10px] text-orange-500">via /welcome page</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-amber-200">
                  <p className="text-2xl font-bold text-amber-600">
                    {analytics.radio_stats.kahl_count}
                  </p>
                  <p className="text-xs text-stone-500">KAHL Radio</p>
                  <p className="text-[10px] text-amber-500">self-reported</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-emerald-200">
                  <p className="text-2xl font-bold text-emerald-600">
                    {analytics.radio_stats.radio_percentage}%
                  </p>
                  <p className="text-xs text-stone-500">of All Leads</p>
                </div>
              </div>

              {/* KPAC Tracking (via /welcome) */}
              <div className="bg-white rounded-lg border border-orange-200 p-4 mb-4">
                <h4 className="font-semibold text-orange-700 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  KPAC Tracking (sanantonioartschool.org → /welcome)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-xl font-bold text-orange-700">
                      {analytics.radio_stats.welcome_page_conversions || 0}
                    </p>
                    <p className="text-xs text-stone-500">/welcome → Contact Form</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-xl font-bold text-orange-600">
                      {analytics.radio_stats.kpac_session_attributed || 0}
                    </p>
                    <p className="text-xs text-stone-500">Session Tracked</p>
                  </div>
                </div>
              </div>

              {/* KAHL Tracking (self-reported) */}
              <div className="bg-white rounded-lg border border-amber-200 p-4 mb-4">
                <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  KAHL Tracking (Self-Reported)
                </h4>
                <p className="text-xs text-stone-500 mb-3">
                  KAHL listeners visit thecoppini.org directly and select "KAHL Radio" in the contact form
                </p>
                <div className="text-center p-3 bg-amber-50 rounded-lg inline-block">
                  <p className="text-xl font-bold text-amber-600">
                    {analytics.radio_stats.kahl_count}
                  </p>
                  <p className="text-xs text-stone-500">Selected "KAHL Radio"</p>
                </div>
              </div>

              {/* How It Works */}
              <div className="mt-4 p-3 bg-stone-100 rounded-lg">
                <p className="text-xs font-medium text-stone-600 mb-2">📍 How Radio Tracking Works:</p>
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white p-2 rounded border border-orange-200">
                    <span className="text-orange-600 font-semibold">KPAC:</span>
                    <p className="text-stone-600 mt-1">
                      sanantonioartschool.org → /welcome<br/>
                      <span className="text-stone-400">Automatic session tracking</span>
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded border border-amber-200">
                    <span className="text-amber-600 font-semibold">KAHL:</span>
                    <p className="text-stone-600 mt-1">
                      Direct to thecoppini.org<br/>
                      <span className="text-stone-400">Self-reported in contact form</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Source Breakdown Chart */}
          <Card data-testid="source-breakdown-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 size={20} />
                Referral Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.by_source?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.by_source.map((source) => {
                    const Icon = sourceIcons[source.source] || MoreHorizontal;
                    const barWidth = (source.count / maxCount) * 100;
                    const colorClass = sourceColors[source.source] || 'bg-gray-500';
                    
                    return (
                      <div key={source.source} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Icon size={16} className="text-stone-500" />
                            <span className="font-medium text-stone-700">{source.label}</span>
                          </div>
                          <span className="text-stone-500">
                            {source.count} ({source.percentage}%)
                          </span>
                        </div>
                        <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colorClass} rounded-full transition-all duration-500`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-8">No data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card data-testid="monthly-trend-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={20} />
                Monthly Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.by_month?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.by_month.map((month) => {
                    const monthName = new Date(month.month + '-01').toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    });
                    const maxMonthCount = Math.max(...analytics.by_month.map(m => m.count));
                    const barWidth = (month.count / maxMonthCount) * 100;
                    
                    return (
                      <div key={month.month} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-stone-700">{monthName}</span>
                          <span className="text-stone-500">{month.count} submissions</span>
                        </div>
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-cafa-red rounded-full transition-all duration-500"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-8">No monthly data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Submissions */}
        <Card className="mt-8" data-testid="recent-submissions-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Mail size={20} />
                Recent Submissions
                {includeSpam && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">
                    Including Spam
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIncludeSpam(v => !v)}
                  data-testid="toggle-spam-btn"
                  className={includeSpam ? 'border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100' : ''}
                >
                  {includeSpam ? (
                    <><EyeOff size={14} className="mr-1" /> Hide Spam</>
                  ) : (
                    <><Eye size={14} className="mr-1" /> Show Spam</>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Spam-marked submissions are hidden from this view and excluded from analytics. Records are kept in the database — nothing is deleted.
            </p>
          </CardHeader>
          <CardContent>
            {submissions.length > 0 ? (
              <div className="divide-y divide-stone-100">
                {submissions.map((sub) => (
                  <div 
                    key={sub.id} 
                    className={`py-4 flex items-start justify-between gap-4 hover:bg-stone-50 -mx-4 px-4 transition-colors ${sub.status === 'spam' ? 'opacity-60 bg-amber-50/40' : ''}`}
                    data-testid={`submission-row-${sub.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-stone-900">{sub.name}</span>
                        <Badge 
                          variant={sub.status === 'new' ? 'default' : 'secondary'}
                          className={
                            sub.status === 'new' ? 'bg-cafa-red' :
                            sub.status === 'spam' ? 'bg-amber-200 text-amber-900 border-amber-300' : ''
                          }
                        >
                          {sub.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-600 truncate">{sub.subject}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                        <span>{sub.email}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          {React.createElement(sourceIcons[sub.referral_source] || MoreHorizontal, { size: 12 })}
                          {sub.referral_source_display}
                        </span>
                        <span>•</span>
                        <span>{formatDate(sub.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedSubmission(sub)}
                        title="View"
                      >
                        <Eye size={16} />
                      </Button>
                      {sub.status === 'new' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => updateStatus(sub.id, 'read')}
                          title="Mark as read"
                        >
                          <CheckCircle size={16} />
                        </Button>
                      )}
                      {sub.status === 'spam' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => unmarkSpam(sub.id)}
                          title="Unmark spam (move back to read)"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          data-testid={`unmark-spam-${sub.id}`}
                        >
                          <CheckCircle size={16} />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSpamTarget(sub)}
                          title="Mark as spam (hides from default view, not deleted)"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          data-testid={`mark-spam-${sub.id}`}
                        >
                          <ShieldAlert size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No submissions yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Submission Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>{selectedSubmission.name}</CardTitle>
                <p className="text-sm text-stone-500">{selectedSubmission.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedSubmission(null)}>
                ✕
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-stone-500">Subject</p>
                <p className="text-stone-900">{selectedSubmission.subject}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-stone-500">How they found us</p>
                <p className="text-stone-900">{selectedSubmission.referral_source_display}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-stone-500">Message</p>
                <p className="text-stone-900 whitespace-pre-wrap">{selectedSubmission.message}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-stone-500">Submitted</p>
                <p className="text-stone-900">{formatDate(selectedSubmission.created_at)}</p>
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button 
                  variant="outline" 
                  className="flex-1 min-w-[120px]"
                  onClick={() => window.location.href = `mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`}
                >
                  <Mail size={16} className="mr-2" />
                  Reply
                </Button>
                {selectedSubmission.status === 'new' && (
                  <Button 
                    className="flex-1 min-w-[120px] bg-cafa-red hover:bg-cafa-red-900"
                    onClick={() => {
                      updateStatus(selectedSubmission.id, 'read');
                      setSelectedSubmission(null);
                    }}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Mark as Read
                  </Button>
                )}
                {selectedSubmission.status === 'spam' ? (
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[120px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => {
                      unmarkSpam(selectedSubmission.id);
                      setSelectedSubmission(null);
                    }}
                    data-testid="modal-unmark-spam-btn"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Unmark Spam
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[120px] border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => setSpamTarget(selectedSubmission)}
                    data-testid="modal-mark-spam-btn"
                  >
                    <ShieldAlert size={16} className="mr-2" />
                    Mark as Spam
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mark as Spam Confirmation */}
      <AlertDialog open={!!spamTarget} onOpenChange={(open) => { if (!open) setSpamTarget(null); }}>
        <AlertDialogContent data-testid="spam-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="text-amber-600" size={20} />
              Mark this submission as spam?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-600 space-y-2">
              <span className="block">
                <strong>{spamTarget?.name}</strong> — {spamTarget?.subject}
              </span>
              <span className="block text-sm">
                Spam submissions are <strong>hidden from the default view</strong> and excluded from analytics. The record stays in the database and is not deleted. You can reverse this from the "Show Spam" view.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="spam-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmMarkSpam}
              data-testid="spam-confirm-btn"
            >
              <ShieldAlert size={14} className="mr-2" />
              Mark as Spam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
